import { useState, useEffect, ChangeEvent } from 'react';
import InputField from './InputField';
import { Card, CollasableCard } from './Cards';
import Converters from './Converters';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';

const cookies = new Cookies();

const HydrometerCorrection = () => {
  const { t } = useTranslation();
  const [measuredBlg, setMeasuredBlg] = useState<number>(() => parseFloat(cookies.get('hydrometer.measuredBlg')) || 12.0);
  const [readingTempInC, setReadingTempInC] = useState<number>(() => parseFloat(cookies.get('hydrometer.readingTempInC')) || 25.0);
  const [calibrationTempInC, setCalibrationTempInC] = useState<number>(() => parseFloat(cookies.get('hydrometer.calibrationTempInC')) || 20.0);
  const [correctedBlg, setCorrectedBlg] = useState<string | null>(null);

  const celsiusToFahrenheit = (celsius: number): number => (celsius * 9 / 5) + 32;

  useEffect(() => {
    document.title = t('hydrometerCorrection.pageTitle');
  }, [t]);

  useEffect(() => {
    const calculateCorrection = () => {
      if (isNaN(measuredBlg) || isNaN(readingTempInC) || isNaN(calibrationTempInC)) {
        setCorrectedBlg(null);
        return;
      }

      const measuredSg = Converters.blgToGravity(measuredBlg);
      if (isNaN(measuredSg)) {
        setCorrectedBlg(null);
        return;
      }

      const tr = celsiusToFahrenheit(readingTempInC);
      const tc = celsiusToFahrenheit(calibrationTempInC);

      const correctedSg = measuredSg * (
        (1.00130346 - 0.000134722124 * tr + 0.00000204052596 * Math.pow(tr, 2) - 0.00000000232820948 * Math.pow(tr, 3)) /
        (1.00130346 - 0.000134722124 * tc + 0.00000204052596 * Math.pow(tc, 2) - 0.00000000232820948 * Math.pow(tc, 3))
      );

      const finalCorrectedBlg = Converters.gravityToPlato(correctedSg);
      if (isNaN(finalCorrectedBlg)) {
        setCorrectedBlg(null);
        return;
      }

      setCorrectedBlg(finalCorrectedBlg.toFixed(2));
    };

    calculateCorrection();
  }, [measuredBlg, readingTempInC, calibrationTempInC]);

  return (
    <div>
      <CollasableCard title={t('hydrometerCorrection.infoTitle')}>
        <p className="text-sm text-muted-foreground mb-2">
          {t('hydrometerCorrection.infoLine1')}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('hydrometerCorrection.infoLine2')}
        </p>
      </CollasableCard>

      <Card title={t('hydrometerCorrection.cardTitle')}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <InputField
            label={t('hydrometerCorrection.measuredGravity')}
            type="number"
            unit="°BLG"
            step={0.1}
            value={measuredBlg}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setMeasuredBlg(parseFloat(e.target.value));
              cookies.set('hydrometer.measuredBlg', e.target.value);
            }}
          />
          <InputField
            label={t('hydrometerCorrection.readingTemp')}
            type="number"
            unit="°C"
            step={0.1}
            value={readingTempInC}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setReadingTempInC(parseFloat(e.target.value));
              cookies.set('hydrometer.readingTempInC', e.target.value);
            }}
          />
          <InputField
            label={t('hydrometerCorrection.calibrationTemp')}
            type="number"
            unit="°C"
            step={0.1}
            value={calibrationTempInC}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setCalibrationTempInC(parseFloat(e.target.value));
              cookies.set('hydrometer.calibrationTempInC', e.target.value);
            }}
          />
          {correctedBlg !== null && (
            <InputField label={t('hydrometerCorrection.correctedGravity')} value={correctedBlg} unit="°BLG" readOnly />
          )}
        </div>
      </Card>
    </div>
  );
};

export default HydrometerCorrection;
